import { useEffect, useState } from 'react';

import { AddIcon, ArrowIcon } from '../../../../assets/svg';
import { ETPAyPagination } from '../../../../components/table/components/pagination';
import { useAuth } from '../../../../hooks/useAuth';
import { useMerchant } from '../../../../hooks/useMerchant';
import { Status } from '../../../../models/common-payments';
import { ColumnInterface } from '../../../../models/table';
import { getRoleLabelById } from '../../../../utils/common';
import {
	HeaderColumn,
	SelectOption,
	ShowPickerContainer,
	SortByContainer,
	Table,
	TableBody,
	TableContainer,
	TableData,
	TableHead,
	TableHeader,
	TableRow,
	TableUndefined,
} from './styles';

interface TableDetailProps {
	data: PaymentsMultiuse[];
	columns?: ColumnInterface<PaymentsMultiuse>[];
}

export interface PaymentsMultiuse {
	trackingkey?: string;
	clabe?: string;
	email?: string;
	reference?: string;
	bankname?: string;
	amount?: string;
	description?: string;
	paymentdate?: string;
	paymenttime?: string;
	finalstatus: Status;
	urlDoc?: string;
	updateddate?: string;
	updatedtime?: string;
}

const options = [
	{
		value: 'all',
		customLabel: 'Todos',
		show: true,
	},
	{
		value: 'schedule',
		customLabel: 'Programado',
		show: true,
	},
	{
		value: 'pending',
		customLabel: 'Pendiente',
		show: true,
	},
	{
		value: 'success',
		customLabel: 'Exitoso',
		show: true,
	},
	{
		value: 'failed',
		customLabel: 'Fallido',
		show: true,
	},
	{
		value: 'cancelled',
		customLabel: 'Cancelado',
		show: true,
	},
];

const TableDetail = ({ data, columns }: TableDetailProps) => {
	const { merchantSelected } = useMerchant();
	const { user } = useAuth();

	const userEtpay =
		(merchantSelected &&
			getRoleLabelById(merchantSelected?.role_id, merchantSelected?.role_id) === 'ETpay') ||
		user?.email.includes('@etpay.com');
	const [modalSelect, setModalSelect] = useState(false);
	const [pagination, setPagination] = useState({ size: 5, page: 1 });
	const [shortBy, setShortBy] = useState({ propertyName: 'paymentdate', short: true });
	const [filterBy, setFilterBy] = useState({ propertyName: '', value: 'all' });

	const startIndex = (pagination.page - 1) * pagination.size;
	const endIndex = pagination.page * pagination.size;
	const dataOrigin = data;
	const [dataFinal, setDataFinal] = useState<PaymentsMultiuse[]>(data.slice(startIndex, endIndex));

	const sortByAmount = async (asc: boolean) => {
		const sortedData = [...dataOrigin].sort((a, b) => {
			const amountA = a.amount ? parseFloat(a.amount.replace(/[$,]/g, '')) : 0;
			const amountB = b.amount ? parseFloat(b.amount.replace(/[$,]/g, '')) : 0;
			return asc ? amountA - amountB : amountB - amountA;
		});
		setDataFinal(sortedData.slice(startIndex, endIndex));
	};

	const sortByPaymentDate = async (asc = true) => {
		const sortedData = [...dataOrigin].sort((a, b) => {
			const dateA = a.paymentdate
				? new Date(a.paymentdate.split('/').reverse().join('-')).getTime()
				: 0;
			const dateB = b.paymentdate
				? new Date(b.paymentdate.split('/').reverse().join('-')).getTime()
				: 0;
			return asc ? dateA - dateB : dateB - dateA;
		});
		setDataFinal(sortedData.slice(startIndex, endIndex));
	};

	const filterSelect = async (propertyName: string, short: boolean) => {
		if (propertyName === 'amount') {
			await sortByAmount(short);
		}
		if (propertyName === 'paymentdate') {
			await sortByPaymentDate(short);
		}
	};

	const filterByStatus = async (status: string) => {
		if (status == 'all') {
			setDataFinal(dataOrigin.slice(startIndex, endIndex));
			return;
		}
		const filteredData = [...dataOrigin].filter((item) => item.finalstatus === status);
		setPagination({ size: 5, page: 1 });
		setDataFinal(filteredData.slice(0 * 5, 5));
	};

	useEffect(() => {
		if (shortBy.propertyName === 'amount' || shortBy.propertyName === 'paymentdate') {
			filterSelect(shortBy.propertyName, shortBy.short);
		}
	}, [shortBy, pagination.page, pagination.size]);

	useEffect(() => {
		if (filterBy.propertyName === 'status') {
			filterByStatus(filterBy.value);
		}
	}, [filterBy]);

	useEffect(() => {
		if (filterBy.value != 'all') {
			setDataFinal(dataFinal.slice(startIndex, endIndex));
		} else {
			filterSelect(shortBy.propertyName, shortBy.short);
		}
	}, [pagination]);

	return (
		<TableContainer>
			<Table>
				<TableHead>
					<TableRow>
						{columns?.map((column: ColumnInterface<PaymentsMultiuse>, i) => (
							<>
								{(column.propertyName !== 'updateddate' || userEtpay) && (
									<TableHeader key={i}>
										<HeaderColumn
											allowSort={
												column.propertyName === 'amount' || column.propertyName === 'paymentdate'
											}
											onClick={() => {
												(column.propertyName === 'amount' ||
													column.propertyName === 'paymentdate') &&
													(setShortBy({ propertyName: column.propertyName, short: !shortBy.short }),
													setFilterBy({
														propertyName: '',
														value: 'all',
													}),
													setModalSelect(false));
												column.propertyName === 'status' && setModalSelect(!modalSelect);
											}}
										>
											{column.title}
											{(column.propertyName === 'amount' ||
												column.propertyName === 'paymentdate') && (
												<SortByContainer
													allowSort={
														column.propertyName === 'amount' ||
														column.propertyName === 'paymentdate'
													}
													ordered={shortBy.short}
													isActive={
														column.propertyName === shortBy.propertyName && filterBy.value == 'all'
													}
												>
													<ArrowIcon />
												</SortByContainer>
											)}
											{column.propertyName === 'status' && (
												<>
													<SortByContainer
														allowSort={column.propertyName === 'status'}
														ordered={false}
														isActive={filterBy.value != 'all'}
													>
														<AddIcon style={{ marginTop: '-6px' }} />
														{modalSelect && (
															<ShowPickerContainer y="bottom" x="left">
																<ul>
																	{options.map((option, i) => (
																		<SelectOption
																			key={i}
																			isSelected={false}
																			onClick={() => {
																				setFilterBy({
																					propertyName: column.propertyName,
																					value: option.value,
																				});
																			}}
																		>
																			{option.customLabel}
																		</SelectOption>
																	))}
																</ul>
															</ShowPickerContainer>
														)}
													</SortByContainer>
												</>
											)}
										</HeaderColumn>
									</TableHeader>
								)}
							</>
						))}
					</TableRow>
				</TableHead>
				<TableBody>
					{dataFinal.length > 0 ? (
						dataFinal.map((item: PaymentsMultiuse, i) => (
							<TableRow key={i}>
								{columns?.map((column: ColumnInterface<PaymentsMultiuse>, j) => (
									<>
										{(column.propertyName !== 'updateddate' || userEtpay) && (
											<TableData key={j}>{column.cellRender(item)}</TableData>
										)}
									</>
								))}
							</TableRow>
						))
					) : (
						<TableUndefined>No hay cargos en este estatus</TableUndefined>
					)}
				</TableBody>
			</Table>
			<ETPAyPagination
				isLoading={false}
				totalResults={filterBy.value == 'all' ? data.length ?? 0 : dataFinal.length ?? 0}
				hasPaginationSelect={false}
				pagination={pagination}
				setPagination={setPagination}
			/>
		</TableContainer>
	);
};

export { TableDetail };
